"use client";

import { createContext, ReactNode, useEffect, useState } from "react";

import { User, UserJSONObject } from "@utils/User/User";
import { createUserFromStrapiUser } from "@utils/Auth/strapiClientSide";
import { fetchStrapiUserProfile } from "@utils/Auth/strapi";
import { createSessionAction } from "@db/createSessionAction";

export interface UserContextInterface {
  user: User | null;
  sessionPayload: UserJSONObject | null;
  setSessionPayload: (sessionPayload: UserJSONObject | null) => void;
}

export const UserContext = createContext<UserContextInterface>({} as UserContextInterface);

export const UserProvider = ({
  children,
  sessionData
}: {
  children: ReactNode;
  sessionData?: UserJSONObject | null;
}) => {
  // User is the name of the "data" that gets stored in context
  const [user, setUser] = useState<User | null>(null);
  const [sessionPayload, setSessionPayload] = useState<UserJSONObject | null>(sessionData || null);

  useEffect(() => {
    const fetchUser = async () => {
      if (user) return;

      if (!sessionPayload) {
        try {
          const user = await fetchStrapiUserProfile();
          setUser(createUserFromStrapiUser(user));
          await createSessionAction(user);
          setSessionPayload(user);
          return;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          setUser(null);
          return;
        }
      }

      const newUser = createUserFromStrapiUser(sessionPayload);
      setUser(newUser);
    };

    fetchUser();
  }, [sessionPayload, user]);

  return <UserContext.Provider value={{ user, sessionPayload, setSessionPayload }}>{children}</UserContext.Provider>;
};
